import { computed, reactive } from 'vue';

/**
 * Centralised selection state for the database list view.
 *
 * Two selection modes coexist:
 *  - Explicit selection: the individual rows the user ticked (`selectedItems`).
 *  - "Select all matching": every result across every page that matches the
 *    current query (`selectAllMatching`), with `excludedIds` holding the rows the
 *    user un-ticked while in this mode (Gmail-style select-all-with-exclusions).
 *
 * The store is the single source of truth and is independent of the currently
 * loaded page, so the selection survives pagination and load-more. Views (the
 * VisivaGrid list) are a one-way projection of this state: user gestures call the
 * actions below, and the grid is re-derived from `isRowSelected()`.
 */
export const createSelectionStore = () => {
    const state = reactive({
        /** @type {Map<string|number, object>} explicit selections (id -> item) */
        selectedItems: new Map(),
        /** @type {Set<string|number>} rows un-ticked while selecting all matching */
        excludedIds: new Set(),
        /** when true, every result across all pages is considered selected */
        selectAllMatching: false,
        /** items currently loaded in the view (current page or all loaded rows) */
        pageItems: [],
    });

    const pageIds = computed(() => state.pageItems
        .map(item => item?.id)
        .filter(id => id !== null && id !== undefined));

    const selectAllMatching = computed(() => state.selectAllMatching);

    const selectedIds = computed(() => Array.from(state.selectedItems.keys()));

    /**
     * Objects available for bulk actions (export selection, raccolta, edit).
     * In "select all matching" mode only the loaded rows can be enumerated, so we
     * expose the loaded page minus the explicit exclusions.
     */
    const selectedObjects = computed(() => {
        if (state.selectAllMatching) {
            return state.pageItems.filter(item => item?.id != null && !state.excludedIds.has(item.id));
        }

        return Array.from(state.selectedItems.values());
    });

    const excludedIds = computed(() => Array.from(state.excludedIds));

    const hasSelection = computed(() => state.selectAllMatching || state.selectedItems.size > 0);

    const isHeaderChecked = computed(() => {
        if (state.selectAllMatching) {
            return state.excludedIds.size === 0;
        }

        if (pageIds.value.length === 0) {
            return false;
        }

        return pageIds.value.every(id => state.selectedItems.has(id));
    });

    const isHeaderIndeterminate = computed(() => {
        if (state.selectAllMatching) {
            return state.excludedIds.size > 0;
        }

        const selectedOnPage = pageIds.value.filter(id => state.selectedItems.has(id)).length;
        return selectedOnPage > 0 && selectedOnPage < pageIds.value.length;
    });

    const isRowSelected = (id) => {
        if (id === null || id === undefined) {
            return false;
        }

        return state.selectAllMatching ? !state.excludedIds.has(id) : state.selectedItems.has(id);
    };

    const toggleRow = (item, selected) => {
        const id = item?.id;
        if (id === null || id === undefined) {
            return;
        }

        if (state.selectAllMatching) {
            if (selected) {
                state.excludedIds.delete(id);
            } else {
                state.excludedIds.add(id);
            }
            return;
        }

        if (selected) {
            state.selectedItems.set(id, item);
        } else {
            state.selectedItems.delete(id);
        }
    };

    const clear = () => {
        state.selectedItems.clear();
        state.excludedIds.clear();
        state.selectAllMatching = false;
    };

    const selectAllAcrossPages = () => {
        state.selectedItems.clear();
        state.excludedIds.clear();
        state.selectAllMatching = true;
    };

    /** Apply the header checkbox value to every matching result. */
    const toggleHeader = (selected = !isHeaderChecked.value) => {
        if (selected) {
            selectAllAcrossPages();
        } else {
            clear();
        }
    };

    const selectPage = () => state.pageItems.forEach(item => toggleRow(item, true));

    const clearPage = () => state.pageItems.forEach(item => toggleRow(item, false));

    const togglePage = () => {
        if (isHeaderChecked.value) {
            clearPage();
        } else {
            selectPage();
        }
    };

    /** Register the rows currently loaded in the view (drives header state). */
    const setPageItems = (items) => {
        state.pageItems = Array.isArray(items) ? items.filter(Boolean) : [];
    };

    return {
        state,
        pageIds,
        selectAllMatching,
        selectedIds,
        selectedObjects,
        excludedIds,
        hasSelection,
        isHeaderChecked,
        isHeaderIndeterminate,
        isRowSelected,
        toggleRow,
        clear,
        selectAllAcrossPages,
        toggleHeader,
        selectPage,
        clearPage,
        togglePage,
        setPageItems,
    };
};

export const selectionStore = createSelectionStore();

export const useSelection = () => selectionStore;
